import {t, plural} from '$utils/lang.js';
// @ts-nocheck
import { query, mutate } from './graphqlClient.js';
import { refreshBasket } from './basketStore.svelte.js';
import { fetchShippingAndPayment } from './paymentStore.svelte.js';

class Address {
    // The oxaddress.OXID this Address represents (server-assigned). Empty for fresh drafts.
    id = $state('');

    countryId = $state('');
    country = $state('');
    countryCode = $state('');

    name = $state('')

    street = $state('');
    streetNo = $state('');
    zip = $state('');
    city = $state('');

    addInfo = $state('');

    company = $state('');
    ustid = $state('');
    phone = $state('');

    // For DHL packstation rows (oxstreet === 'Packstation'): the actual physical
    // street of the station (e.g. "Habsburgerstr. 19"). Stored in oxaddress.M7_PACKSTATION_STREET.
    packstationStreet = $state('');

    // Google Address Validation (AMS) — mirrors backend MOJOAMS* columns.
    // amsStatus drives the validation modal in User.svelte (Phase 3).
    amsStatus = $state(null);
    amsSource = $state(null);
    // Raw predictions JSON (verdict + Google postalAddress). Frontend parses
    // with parsePredictions() in lib/addressValidation.js when rendering the
    // correction modal.
    amsPredictions = $state(null);

    constructor(data = {}) {
        this.update(data);
    }

    reset() {
        this.id = '';
        this.countryId = '';
        this.country = '';
        this.countryCode = '';
        this.name = '';
        this.street = '';
        this.streetNo = '';
        this.zip = '';
        this.city = '';
        this.addInfo = '';
        this.company = '';
        this.ustid = '';
        this.phone = '';
        this.packstationStreet = '';
        this.amsStatus = null;
        this.amsSource = null;
        this.amsPredictions = null;
    }

    update(data) {
        if (!data) return;
        // Don't use Object.assign — Svelte 5 transforms class fields with `$state`
        // into prototype getter/setters, so they're NOT own enumerable properties.
        // Object.assign would skip them entirely. Iterate explicitly instead.
        const keys = ['id', 'countryId', 'country', 'countryCode', 'name', 'street', 'streetNo',
                      'zip', 'city', 'addInfo', 'company', 'ustid', 'phone', 'packstationStreet',
                      'amsStatus', 'amsSource', 'amsPredictions'];
        for (const key of keys) {
            if (data[key] !== undefined) this[key] = data[key];
        }
    }
}

class User {
    id = $state('');

    email = $state('')
    password = $state('')
    isLoggedIn = $state(false)
    hasPassword = $state(false)

    newsletter = $state(false)

    addressType = $state('address')
    hideShipping = $state(true)

    billingAddress = $state(new Address())
    shippingAddress = $state(new Address())

    sid = '';

    reset() {
        this.id = '';
        this.email = '';
        this.password = '';
        this.isLoggedIn = false;
        this.newsletter = false;
        // hideShipping and addressType are NOT reset here — they're derived from
        // the active delivery selection in User.svelte. Resetting them on fetchUser
        // would race with the sync $effect and cause stale values to be sent in
        // the next saveUser call.
        this.sid = '';

        this.billingAddress.reset();
        this.shippingAddress.reset();
    }

    fill(data) {
        this.reset();

        if (!data) return;

        const { billingAddress, shippingAddress, ...rest } = data;

        // Object.assign skips Svelte 5 $state prototype fields — set explicitly
        if (rest.id !== undefined) this.id = rest.id;
        if (rest.email !== undefined) this.email = rest.email;
        if (rest.newsletter !== undefined) this.newsletter = !!rest.newsletter;
        this.isLoggedIn = !!data.isLoggedIn;
        this.hasPassword = !!data.hasPassword;
        this.sid = rest.sid ?? '';

        if (billingAddress) this.billingAddress.update(billingAddress);
        if (shippingAddress) this.shippingAddress.update(shippingAddress);
    }
}

// --- GraphQL Queries & Mutations ---
const ADDRESS_FIELDS = 'id name street streetNo zip city country countryId countryCode company ustid addInfo phone packstationStreet amsStatus amsSource amsPredictions';

const CHECKOUT_USER_QUERY = `query CheckoutUser { checkoutUser { id email isLoggedIn hasPassword newsletter billingAddress { ${ADDRESS_FIELDS} } shippingAddress { ${ADDRESS_FIELDS} } deliveryAddresses { ${ADDRESS_FIELDS} } } }`;

const LOGIN_MUTATION = `mutation LoginUser($email: String!, $password: String!) { loginUser(email: $email, password: $password) { success errors { field message code minutes } user { id email isLoggedIn hasPassword newsletter billingAddress { ${ADDRESS_FIELDS} } shippingAddress { ${ADDRESS_FIELDS} } deliveryAddresses { ${ADDRESS_FIELDS} } } } }`;

const LOGOUT_MUTATION = `mutation LogoutUser { logoutUser }`;

const SAVE_USER_MUTATION = `mutation SaveCheckoutUser($input: CheckoutUserInput!) { saveCheckoutUser(input: $input) { success errors { field message code minutes } user { id email isLoggedIn newsletter billingAddress { ${ADDRESS_FIELDS} } shippingAddress { ${ADDRESS_FIELDS} } deliveryAddresses { ${ADDRESS_FIELDS} } } } }`;

const DELIVERY_ADDRESSES_QUERY = `query DeliveryAddresses { deliveryAddresses { ${ADDRESS_FIELDS} } }`;

const COUNTRIES_QUERY = `query ActiveCountries { activeCountries { id title } }`;
const MARKET_COUNTRY_QUERY = `query MarketCountry { marketCountry { id title code } }`;

const SELECT_ADDRESS_MUTATION = `mutation SelectDeliveryAddress($addressId: String!) { selectDeliveryAddress(addressId: $addressId) }`;

const DELETE_ADDRESS_MUTATION = `mutation DeleteDeliveryAddress($addressId: String!) { deleteDeliveryAddress(addressId: $addressId) }`;

// --- Internal State ---
const store = $state({
    user: new User(),
    errors: {},
    deliveryAddresses: [],
    selectedDeliveryAddressId: null,
    countries: [],
    loading: false,
    saving: false,
    loginError: '',
    /** @type {{ taxInfo: number, messageStr: string, checks: Array<{label: string, ok: boolean, message?: string}>|null } | null} */
    vatValidationResult: null,
});

// --- Helper: convert FieldError[] to { field: message } map ---
// Meldungen des Proxys sind deutsche Saetze = Schluessel im Woerterbuch.
// reference: [oxid-proxy modules/user/service 1] — mit Zahl im Satz kommt ein code.
function meldung(e) {
    if (e?.code === 'LOGIN_LOCKED') {
        return t(plural(e.minutes, {
            one: 'Zu viele Login-Versuche. Bitte in {minutes} Minute erneut probieren.',
            other: 'Zu viele Login-Versuche. Bitte in {minutes} Minuten erneut probieren.',
        }), {minutes: e.minutes});
    }
    return t(e?.message ?? '');
}

function toErrorMap(errors) {
    const map = {};
    if (Array.isArray(errors)) {
        errors.forEach(e => { map[e.field] = meldung(e); });
    }
    return map;
}

// --- API layer ---
const api = {
    fetchUser: async () => {
        const data = await query(CHECKOUT_USER_QUERY);
        return data.checkoutUser;
    },
    login: async (email, password) => {
        const data = await mutate(LOGIN_MUTATION, { email, password });
        return data.loginUser;
    },
    logout: async () => {
        const data = await mutate(LOGOUT_MUTATION);
        return data.logoutUser;
    },
    saveUser: async (input) => {
        const data = await mutate(SAVE_USER_MUTATION, { input });
        return data.saveCheckoutUser;
    },
    fetchCountries: async () => {
        const data = await query(COUNTRIES_QUERY);
        return data.activeCountries;
    },
    fetchMarketCountry: async () => {
        const data = await query(MARKET_COUNTRY_QUERY);
        return data.marketCountry;
    },
    fetchDeliveryAddresses: async () => {
        const data = await query(DELIVERY_ADDRESSES_QUERY);
        return data.deliveryAddresses;
    },
    selectAddress: async (addressId) => {
        const data = await mutate(SELECT_ADDRESS_MUTATION, { addressId });
        return data.selectDeliveryAddress;
    },
    deleteAddress: async (addressId) => {
        const data = await mutate(DELETE_ADDRESS_MUTATION, { addressId });
        return data.deleteDeliveryAddress;
    },
};

// --- Actions ---
const applyUserResult = (result) => {
    if (!result) return;
    if (result.user) {
        store.user.fill(result.user);
        if (result.user.deliveryAddresses) {
            store.deliveryAddresses = result.user.deliveryAddresses;
        }
    }
    store.errors = result.errors ? toErrorMap(result.errors) : {};
};

const fetchUser = async () => {
    store.loading = true;
    store.errors = {};
    try {
        const userData = await api.fetchUser();
        if (userData) {
            store.user.fill(userData);
            if (userData.deliveryAddresses) {
                store.deliveryAddresses = userData.deliveryAddresses;
            }
        }
    } catch (e) {
        store.errors = { general: e.message };
    } finally {
        store.loading = false;
    }
    return store.user;
};

const login = async () => {
    store.loading = true;
    store.loginError = '';
    store.errors = {};
    try {
        const result = await api.login(store.user.email, store.user.password);
        if (result.success) {
            // Trigger a PHP request so the login queue gets processed. We hit
            // ?cl=basket because BasketController is in _aRequireSessionWithParams
            // (m7_checkout Session.php) and isn't cacheable — Apache happily
            // serves a stale 200 for ?cl=start, which silently skips the
            // queue-processor and leaves isLoggedIn=false until a full reload.
            await fetch('/index.php?cl=basket', {credentials: 'include'});
            // Now fetch the fully authenticated user data
            const userData = await api.fetchUser();
            if (userData) {
                store.user.fill(userData);
                if (userData.deliveryAddresses) {
                    store.deliveryAddresses = userData.deliveryAddresses;
                }
            }
        } else {
            store.loginError = (result.errors?.[0] && meldung(result.errors[0])) || t('Login fehlgeschlagen.');
            store.errors = toErrorMap(result.errors);
        }
    } catch (e) {
        store.loginError = e.message;
    } finally {
        store.loading = false;
    }
    return store.user;
};

const logout = async () => {
    store.loading = true;
    try {
        await api.logout();
        store.user.reset();
        store.deliveryAddresses = [];
        store.errors = {};
        store.loginError = '';
    } catch (e) {
        store.errors = { general: e.message };
    } finally {
        store.loading = false;
    }
};

const saveUser = async () => {
    store.saving = true;
    store.errors = {};
    try {
        const u = store.user;
        // `u.hideShipping` is the explicit intent ("use billing as shipping").
        // `selectedDeliveryAddressId` is orthogonal — it can be null while
        // hideShipping=false (= INSERT a new oxaddress), or set (= UPDATE that row).
        const useBillingAsShipping = u.hideShipping;
        const input = {
            email: u.email,
            newsletter: u.newsletter,
            addressType: u.addressType,
            useShippingAsBilling: useBillingAsShipping,
            // Even when hideShipping=true we DON'T pass an id — backend treats
            // (useBilling=true && deliveryAddressId set) as "sync billing into delivery"
            // which we removed, but still safer to keep them mutually exclusive.
            deliveryAddressId: useBillingAsShipping ? null : (store.selectedDeliveryAddressId || null),
            billingAddress: {
                name: u.billingAddress.name,
                street: u.billingAddress.street,
                streetNo: u.billingAddress.streetNo,
                zip: u.billingAddress.zip,
                city: u.billingAddress.city,
                countryId: u.billingAddress.countryId,
                company: u.billingAddress.company,
                ustid: u.billingAddress.ustid,
                addInfo: u.billingAddress.addInfo,
                phone: u.billingAddress.phone,
            },
        };

        if (!useBillingAsShipping) {
            // Defensive: if the in-memory shippingAddress has no usable data,
            // sending it as `useShippingAsBilling=false + deliveryAddressId=null`
            // would make the backend INSERT a blank oxaddress (validation error)
            // or, worse, create a new delivery row from billing-edit mirror data.
            // Treat that state as "use billing as shipping" instead.
            const s = u.shippingAddress;
            const hasShippingData = !!(s.name || s.street || s.zip || s.city);
            if (!hasShippingData && !store.selectedDeliveryAddressId) {
                input.useShippingAsBilling = true;
            } else {
                input.shippingAddress = {
                    name: s.name,
                    street: s.street,
                    streetNo: s.streetNo,
                    zip: s.zip,
                    city: s.city,
                    countryId: s.countryId,
                    company: s.company,
                    ustid: s.ustid,
                    addInfo: s.addInfo,
                    phone: s.phone,
                    packstationStreet: s.packstationStreet || '',
                };
            }
        }

        const result = await api.saveUser(input);
        applyUserResult(result);

        if (result.success) {
            // Force OXID to recalculate the PHP basket (shipping costs + VAT)
            // for the updated billing/delivery country BEFORE we read it back.
            // This used to be gated on `!useBillingAsShipping`, which skipped
            // the recalc on the "Lieferadresse = Rechnungsadresse" toggle — the
            // PHP basket then kept the stale VAT until a full page reload. The
            // proxy computes shipping/VAT off the PHP session, so the recalc has
            // to fire on every successful save, not only for separate delivery.
            await fetch('/index.php?cl=m7-checkout-dbc&fnc=recalculateBasket', {credentials: 'include'}).catch(() => {});
            // Then refresh basket — proxy applies VAT-free prices for non-EU
            // countries (e.g. CH) based on the updated billing/delivery country.
            // Also re-fetch shipping methods: a country change can lock/unlock
            // shipping sets (e.g. PL → DE re-enables the regular options).
            await Promise.all([refreshBasket(), fetchShippingAndPayment()]);
        }

        return result.success;
    } catch (e) {
        store.errors = { general: e.message };
        return false;
    } finally {
        store.saving = false;
    }
};

const fetchCountries = async () => {
    try {
        store.countries = await api.fetchCountries();
    } catch (e) {
        store.errors = { general: e.message };
    }
    return store.countries;
};

// Name eines Landes aus der Länderliste des Proxys, in der Sprache des Markts. reference: [1]
let laenderLaden = null;
const countryName = async (countryId) => {
    if (!store.countries?.length) {
        laenderLaden ??= fetchCountries().finally(() => { laenderLaden = null; });
        await laenderLaden;
    }
    return (store.countries ?? []).find((c) => c.id === countryId)?.title ?? '';
};

// Standardland des Markts ({id, title, code}), einmal je Seite. reference: [1]
let marktLandLaden = null;
// decision: [2]
const ladeMarktLand = async () => {
    for (let versuch = 1; versuch <= 2; versuch++) {
        try {
            const land = await api.fetchMarketCountry();
            if (land) return land;
        } catch (e) {
            // retried once below
        }
        if (versuch < 2) await new Promise((r) => setTimeout(r, 800));
    }
    return null;
};
const fetchMarketCountry = () => {
    marktLandLaden ??= ladeMarktLand().then((land) => {
        if (!land) marktLandLaden = null;
        return land;
    });
    return marktLandLaden;
};

const fetchDeliveryAddresses = async () => {
    try {
        store.deliveryAddresses = await api.fetchDeliveryAddresses();
    } catch (e) {
        store.errors = { general: e.message };
    }
    return store.deliveryAddresses;
};

const selectAddress = async (addressId) => {
    try {
        await api.selectAddress(addressId);
        // Recalculate the PHP basket for the newly selected delivery address
        // BEFORE reading it back — otherwise refreshBasket reads a stale VAT /
        // shipping cost until a reload (same root cause as the saveUser
        // billing-as-shipping recalc-skip bug).
        await fetch('/index.php?cl=m7-checkout-dbc&fnc=recalculateBasket', {credentials: 'include'}).catch(() => {});
        // Refresh basket so easyCreditInterests reflects backend invalidation
        // (proxy clears EasyCreditStorage on address change), AND re-fetch
        // shipping methods — the new country may unlock/lock shipping sets.
        await Promise.all([refreshBasket(), fetchShippingAndPayment()]);
    } catch (e) {
        store.errors = { general: e.message };
    }
};

const deleteAddress = async (addressId) => {
    try {
        await api.deleteAddress(addressId);
        // Optimistic local removal so the UI updates immediately.
        store.deliveryAddresses = store.deliveryAddresses.filter(a => a.id !== addressId);
        if (store.selectedDeliveryAddressId === addressId) {
            store.selectedDeliveryAddressId = null;
        }
    } catch (e) {
        store.errors = { general: e.message };
    }
};

const DE_COUNTRY_ID = 'a7c40f631fc920687.20179984';

const validateVatId = async () => {
    const ustid = store.user?.billingAddress?.ustid;
    const countryId = store.user?.billingAddress?.countryId;
    if (!ustid || !countryId || countryId === DE_COUNTRY_ID) {
        store.vatValidationResult = null;
        return;
    }
    try {
        const params = new URLSearchParams({cl: 'm7-checkout-doc', fnc: 'validateVatId'});
        const response = await fetch(`/index.php?${params}`, {credentials: 'include'});
        const data = await response.json().catch(() => null);
        if (data) store.vatValidationResult = data;
    } catch (_) {}
};

export {
    store,
    login,
    logout,
    fetchUser,
    saveUser,
    validateVatId,
    fetchCountries,
    countryName,
    fetchMarketCountry,
    fetchDeliveryAddresses,
    selectAddress,
    deleteAddress,
    Address,
    User
}
